import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../supabaseClient';

type AuthModalProps = {
  onClose: () => void;
  initialMode?: 'signin' | 'signup';
  initialInvitationToken?: string;
};

const INVITE_PREVIEW_FAIL = 'This invitation is invalid, expired, or has already been used.';
const INVITE_SIGNUP_FAIL =
  'WatchWicket is currently in private beta. A valid invitation is required to create an account.';
const PRIVATE_BETA_NOTICE =
  'WatchWicket is currently in private beta. New accounts require an invitation.';

function getPasswordStrength(password: string): { strength: number; text: string; color: string } {
  if (password.length === 0) return { strength: 0, text: '', color: '' };

  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
  if (/\d/.test(password)) strength++;
  if (/[^a-zA-Z0-9]/.test(password)) strength++;

  if (strength <= 1) return { strength, text: 'Weak', color: 'text-red-400' };
  if (strength <= 3) return { strength, text: 'Medium', color: 'text-yellow-400' };
  return { strength, text: 'Strong', color: 'text-green-400' };
}

export default function AuthModal({ onClose, initialMode, initialInvitationToken }: AuthModalProps) {
  const [mode, setMode] = useState<'signin' | 'signup' | 'reset'>(initialMode || 'signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [invitationCode, setInvitationCode] = useState(initialInvitationToken || '');
  const [invitationChecked, setInvitationChecked] = useState(false);
  const [invitationValid, setInvitationValid] = useState(false);
  const [checkingInvitation, setCheckingInvitation] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signUp, resetPassword, signInWithGoogle } = useAuth();

  useEffect(() => {
    if (initialInvitationToken) {
      setMode('signup');
      setInvitationCode(initialInvitationToken);
    }
  }, [initialInvitationToken]);

  useEffect(() => {
    const trimmedCode = invitationCode.trim();
    const trimmedEmail = email.trim();
    if (mode !== 'signup' || !trimmedCode || !trimmedEmail) {
      setInvitationChecked(false);
      setInvitationValid(false);
      return;
    }

    let cancelled = false;
    setCheckingInvitation(true);

    const timeout = setTimeout(async () => {
      try {
        const { data, error: rpcError } = await supabase.rpc('preview_invitation', {
          p_token: trimmedCode,
          p_email: trimmedEmail,
        });
        if (cancelled) return;
        if (rpcError) {
          setInvitationChecked(true);
          setInvitationValid(false);
        } else {
          setInvitationChecked(true);
          setInvitationValid(data === true);
        }
      } catch (err) {
        if (cancelled) return;
        setInvitationChecked(true);
        setInvitationValid(false);
      } finally {
        if (!cancelled) setCheckingInvitation(false);
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [invitationCode, email, mode]);

  const passwordStrength = mode === 'signup' ? getPasswordStrength(password) : null;

  const handleCompleteSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      if (!fullName.trim()) {
        setError('Please enter your full name');
        setLoading(false);
        return;
      }
      if (password.length < 8) {
        setError('Password must be at least 8 characters long');
        setLoading(false);
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      const trimmedCode = invitationCode.trim();
      if (!trimmedCode) {
        setError('Please enter your invitation code');
        setLoading(false);
        return;
      }

      const { error: signUpError, needsConfirmation } = await signUp(email, password, fullName, trimmedCode);
      if (signUpError) {
        console.error('[AuthModal] Signup error:', signUpError);
        const msg = (signUpError.message || '').toLowerCase();
        if (msg.includes('invitation') || msg.includes('registration is by invitation')) {
          setError(INVITE_SIGNUP_FAIL);
        } else {
          setError(signUpError.message);
        }
      } else if (needsConfirmation) {
        setSuccess('Account created. Please check your email for a confirmation link before signing in.');
        setTimeout(() => {
          setMode('signin');
          setPassword('');
          setConfirmPassword('');
          setSuccess('Account created. Check your email to confirm, then sign in.');
        }, 4000);
      } else {
        setSuccess('Account created! You can now sign in.');
        setTimeout(() => {
          setMode('signin');
          setPassword('');
          setConfirmPassword('');
          setSuccess('Account created! You can now sign in.');
        }, 2000);
      }
    } catch (err) {
      console.error('[AuthModal] Signup exception:', err);
      const errorMsg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error: signInError } = await signIn(email, password);

      if (signInError) {
        console.error('[AuthModal] Sign in error:', signInError);
        setError(signInError.message);
      } else {
        onClose();
      }
    } catch (err) {
      console.error('[AuthModal] Sign in exception:', err);
      const errorMsg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { error: resetError } = await resetPassword(email);
      if (resetError) {
        console.error('[AuthModal] Reset password error:', resetError);
        setError(resetError.message);
      } else {
        setSuccess('Password reset email sent! Check your inbox.');
      }
    } catch (err) {
      console.error('[AuthModal] Reset password exception:', err);
      const errorMsg = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      const { error: googleError } = await signInWithGoogle();
      if (googleError) {
        console.error('[AuthModal] Google sign in error:', googleError);
        setError(`Google Sign In failed: ${googleError.message}`);
        setLoading(false);
      }
    } catch (err) {
      console.error('[AuthModal] Google sign in exception:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to sign in with Google';
      setError(`Exception: ${errorMsg}`);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl p-6 max-w-md w-full shadow-2xl border border-slate-700 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white">
            {mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
          </h2>
          <button
            onClick={() => {
              onClose();
              setEmail('');
              setPassword('');
              setConfirmPassword('');
              setFullName('');
              setInvitationCode('');
              setError('');
              setSuccess('');
            }}
            className="text-gray-400 hover:text-white text-3xl font-light"
          >
            ×
          </button>
        </div>

        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Password *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-sm"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-red-400 text-xs bg-red-900 bg-opacity-30 p-3 rounded-lg border border-red-800 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-400 text-sm bg-green-900 bg-opacity-30 p-3 rounded-lg border border-green-800">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              {loading ? 'Processing...' : 'Sign In'}
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <form onSubmit={handleCompleteSignUp} className="space-y-4">
            <div className="bg-amber-900 bg-opacity-20 border border-amber-700 rounded-lg p-3">
              <p className="text-amber-300 text-xs">{PRIVATE_BETA_NOTICE}</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Full Name *</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Password *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-sm"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {passwordStrength && password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded ${
                          i < passwordStrength.strength
                            ? passwordStrength.strength <= 1
                              ? 'bg-red-500'
                              : passwordStrength.strength <= 3
                              ? 'bg-yellow-500'
                              : 'bg-green-500'
                            : 'bg-slate-600'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs ${passwordStrength.color}`}>
                    Password strength: {passwordStrength.text}
                  </p>
                </div>
              )}
              <p className="text-xs text-slate-400 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Confirm Password *</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={8}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white pr-12"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white text-sm"
                >
                  {showConfirmPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Invitation Code *</label>
              <input
                type="text"
                value={invitationCode}
                onChange={(e) => { setInvitationCode(e.target.value); setInvitationChecked(false); setInvitationValid(false); }}
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white"
                placeholder="Enter your invitation code"
                autoCapitalize="none"
                autoCorrect="off"
              />
              {checkingInvitation && (
                <p className="text-xs text-slate-400 mt-1">Checking invitation...</p>
              )}
              {!checkingInvitation && invitationChecked && invitationValid && (
                <p className="text-xs text-green-400 mt-1">Invitation looks valid.</p>
              )}
              {!checkingInvitation && invitationChecked && !invitationValid && email.trim() && invitationCode.trim() && (
                <p className="text-xs text-red-400 mt-1">{INVITE_PREVIEW_FAIL}</p>
              )}
              {!checkingInvitation && !invitationChecked && !invitationCode.trim() && (
                <p className="text-xs text-slate-400 mt-1">Required for private beta signup</p>
              )}
            </div>

            {error && (
              <div className="text-red-400 text-xs bg-red-900 bg-opacity-30 p-3 rounded-lg border border-red-800 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-400 text-sm bg-green-900 bg-opacity-30 p-3 rounded-lg border border-green-800">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </button>
          </form>
        )}

        {mode === 'signup' && (
          <div className="mt-2">
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-800 text-slate-400">Or sign up with</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-100 disabled:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </button>
            <p className="text-xs text-slate-400 text-center mt-3">
              During private beta, use the Google account matching the email your invitation was sent to.
            </p>
          </div>
        )}

        {mode === 'reset' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <p className="text-slate-300 text-sm mb-4">
              Enter your email and we'll send you a link to reset your password.
            </p>

            <div>
              <label className="block text-sm font-medium mb-2 text-slate-200">Email *</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-white"
                placeholder="you@example.com"
              />
            </div>

            {error && (
              <div className="text-red-400 text-xs bg-red-900 bg-opacity-30 p-3 rounded-lg border border-red-800 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
                {error}
              </div>
            )}

            {success && (
              <div className="text-green-400 text-sm bg-green-900 bg-opacity-30 p-3 rounded-lg border border-green-800">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold py-3 px-4 rounded-lg transition-colors"
            >
              {loading ? 'Sending...' : 'Send Reset Email'}
            </button>
          </form>
        )}

        {mode === 'signin' && (
          <>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-800 text-slate-400">Or continue with</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full bg-white hover:bg-gray-100 disabled:bg-gray-300 text-gray-800 font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </button>
            <p className="text-xs text-slate-400 text-center mt-3">
              New here? During private beta, use the Google account matching the email your invitation was sent to.
            </p>
          </>
        )}

        <div className="mt-6 text-center text-sm space-y-2">
          {mode === 'signin' && (
            <>
              <p className="text-slate-300">
                Don't have an account?{' '}
                <button
                  onClick={() => { setMode('signup'); setError(''); setSuccess(''); }}
                  className="text-green-400 hover:text-green-300 font-medium"
                >
                  Sign up
                </button>
              </p>
              <p>
                <button
                  onClick={() => { setMode('reset'); setError(''); setSuccess(''); }}
                  className="text-blue-400 hover:text-blue-300 font-medium"
                >
                  Forgot password?
                </button>
              </p>
            </>
          )}
          {mode === 'signup' && (
            <p className="text-slate-300">
              Already have an account?{' '}
              <button
                onClick={() => { setMode('signin'); setError(''); setSuccess(''); }}
                className="text-green-400 hover:text-green-300 font-medium"
              >
                Sign in
              </button>
            </p>
          )}
          {mode === 'reset' && (
            <p>
              <button
                onClick={() => { setMode('signin'); setError(''); setSuccess(''); }}
                className="text-green-400 hover:text-green-300 font-medium"
              >
                Back to sign in
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
